'use client';

import { useState } from 'react';
import { userProfileService, UserProfile } from '@/lib/services/userProfileService';

interface ProfileManagementProps {
  profile: UserProfile;
  application: any;
  onUpdate: () => void;
}

export default function ProfileManagement({ profile, application, onUpdate }: ProfileManagementProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    bio: profile.bio || '',
    preferredContactMethod: profile.preferredContactMethod || 'email',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await userProfileService.updateProfile(profile.id!, {
        bio: formData.bio,
        preferredContactMethod: formData.preferredContactMethod,
      });

      alert('Profile updated successfully!');
      setIsEditing(false);
      onUpdate();
    } catch (error: any) {
      alert(`Failed to update profile: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Profile Header */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-dark-navy">Profile Information</h2>
          <button
            onClick={() => setIsEditing(!isEditing)}
            className="px-4 py-2 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors"
          >
            {isEditing ? 'Cancel' : 'Edit Profile'}
          </button>
        </div>

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-dark-navy mb-2">Bio</label>
              <textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                rows={4}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
                placeholder="Tell us about yourself..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dark-navy mb-2">
                Preferred Contact Method
              </label>
              <select
                value={formData.preferredContactMethod}
                onChange={(e) => setFormData({ ...formData, preferredContactMethod: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-purple focus:border-transparent"
              >
                <option value="email">Email</option>
                <option value="phone">Phone</option>
                <option value="sms">SMS</option>
              </select>
            </div>

            <div className="flex space-x-4">
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-3 bg-brand-purple text-white rounded-lg hover:bg-opacity-90 transition-colors disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </button>
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="px-6 py-3 bg-gray-200 text-dark-navy rounded-lg hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-body-text mb-2">Bio</p>
              <p className="text-dark-navy">{profile.bio || 'No bio provided yet.'}</p>
            </div>

            <div>
              <p className="text-sm text-body-text mb-2">Preferred Contact Method</p>
              <p className="text-dark-navy font-medium capitalize">{profile.preferredContactMethod || 'Email'}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-gray-200">
              <div>
                <p className="text-sm text-body-text mb-1">Verification Status</p>
                <p className="text-dark-navy font-medium">
                  {profile.isVerified ? '✓ Verified' : 'Pending Verification'}
                </p>
              </div>
              <div>
                <p className="text-sm text-body-text mb-1">Account Status</p>
                <p className="text-dark-navy font-medium">
                  {profile.isActive ? '✓ Active' : 'Inactive'}
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Application Details (Read-only) */}
      <div className="bg-white rounded-2xl shadow-card p-6">
        <h2 className="text-xl font-bold text-dark-navy mb-4">Application Details</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <p className="text-sm text-body-text mb-1">Full Name</p>
            <p className="text-dark-navy font-medium">{application?.firstName} {application?.lastName}</p>
          </div>
          <div>
            <p className="text-sm text-body-text mb-1">Email</p>
            <p className="text-dark-navy font-medium">{application?.email}</p>
          </div>
          <div>
            <p className="text-sm text-body-text mb-1">Phone</p>
            <p className="text-dark-navy font-medium">{application?.phone}</p>
          </div>
          <div>
            <p className="text-sm text-body-text mb-1">Address</p>
            <p className="text-dark-navy font-medium">
              {application?.address}, {application?.city}, {application?.province} {application?.postalCode}
            </p>
          </div>
          <div>
            <p className="text-sm text-body-text mb-1">Emergency Contact</p>
            <p className="text-dark-navy font-medium">
              {application?.emergencyContactName} ({application?.emergencyContactRelationship})
            </p>
          </div>
          <div>
            <p className="text-sm text-body-text mb-1">Emergency Phone</p>
            <p className="text-dark-navy font-medium">{application?.emergencyContactPhone}</p>
          </div>
        </div>
      </div>
    </div>
  );
}